import React, { useMemo, useState } from 'react';
import { BubbleBadge, BubbleButton, BubbleTable } from '../BubbleUI';
import {
  generateFormattedTimeString,
  getBasicTimeString,
  getEventVenueDisplay,
  makeBareEventSorter,
  populateEvent,
} from '@go-shared/lib/event';
import { mapBy, notFalsy, notUndefined } from '@go-shared/lib/functional';
import { BubbleTableFields, BubbleTableTemplate } from '../BubbleUI/BubbleTable';
import {
  isHiddenEventDueToBasicFilters,
  evaluateMcmEndpoint,
  evaluateMcmTruthyAggregate,
  toggleMcmEndpoint,
} from '@go-shared/lib/mcm';
import { mcmEndpointToIcon } from '@go-client/lib/mcm';
import { EventTemplate } from '@go-shared/types/eventTemplate';
import { Event } from '@go-shared/types/event';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import { CommonTextTranslation, EventTemplateTranslation } from '@go-shared/types/translation';
import { Plan } from '@go-shared/types/plan';
import { BrandSegment, HourFormat } from '@go-shared/types/brand';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { DailySchedulePanelMode } from './DailySchedulePanel';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { DEFAULT_DAY_START } from '@go-shared/lib/property';
import { DEFAULT_TIMESTR_FORMAT, timeToMinutes } from '@go-shared/lib/time';
import { GroupEvent } from '@go-shared/types/groupEvent';
import GroupsGlance from '../GroupsGlance';
import EventsGlance from '../EventsGlance';
import { StaffTemplate } from '@go-shared/types/staffTemplate';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { evaluateMergedVars, hasAllMergedVarsSet } from '@go-shared/lib/mergedVars';
import { findIconDefinition } from '@fortawesome/fontawesome-svg-core';
import Fuse from 'fuse.js';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import { SailingDayBundle } from '@go-shared/types/sailing';
import { translateEventTemplate } from '@go-shared/lib/eventTemplate';
import { getCommonTexts } from '@go-shared/lib/brand';
import { evaluateAudienceTruthyAggregate, evaluateAudienceZone } from '@go-shared/lib/audience';
import BubbleMergedVarHint from '../BubbleUI/BubbleMergedVarHint';

interface EventTableItemBase {
  id: string;
  title: string;
  description: string;
  venue: string;
  startTime: string;
  endTime: string;
  segment?: BrandSegment;
}

interface EventTableItemPlanEvent {
  type: 'event';
  _data: {
    event: Event;
    eventTemplate: EventTemplate;
  };
}

interface EventTableItemPlanGroupEvent {
  type: 'groupEvent';
  _data: {
    event: GroupEvent;
  };
}

type EventTableItem = EventTableItemBase & (EventTableItemPlanEvent | EventTableItemPlanGroupEvent);

interface DailyEventListPanelPropsBase {
  mode: DailySchedulePanelMode;
  width: number;
  locked: boolean;
  search: string;
  focusEdp: string;
  focusZone: string | undefined;
  focusLang: string | undefined;
  mcmVisibleEdps: string[];
  plan: Plan;
  hourFormat: HourFormat;
  groupEvts: GroupEvent[];
  eventTemplates: EventTemplate[];
  venueTemplates: VenueTemplate[];
  staffTemplates: StaffTemplate[];
  eventTemplateTranslations: EventTemplateTranslation[];
  commonTextTranslations: CommonTextTranslation[];
  dayBundle?: SailingDayBundle;
  onUpdateEvent: (event: Event, applyStaffToPlan?: boolean, transferToTargetDay?: string) => void;
  onEditEvent: (event: Event) => void;
  onDeleteEvent: (eventId: string) => void;
}

type DailyEventListPanelProps = DailyEventListPanelPropsBase & React.HTMLAttributes<HTMLDivElement>;

function DailyEventListPanel(props: DailyEventListPanelProps) {
  const {
    mode,
    width,
    locked,
    search,
    focusEdp,
    focusLang,
    focusZone,
    mcmVisibleEdps,
    plan,
    hourFormat,
    groupEvts,
    eventTemplates,
    venueTemplates,
    staffTemplates,
    eventTemplateTranslations,
    commonTextTranslations,
    dayBundle,
    onUpdateEvent,
    onDeleteEvent,
    onEditEvent,
    ...rest
  } = props;

  const serverInfo = useServerInfo();
  const { activeProperty: property } = useActiveProperty();
  const { user } = useUserInfo();

  const [expandedReqId, setExpandedReqId] = useState<string | undefined>(undefined);
  const [showDelete, setShowDelete] = useState<{ [key: string]: boolean }>({});

  const dayStart = property.dayStart || DEFAULT_DAY_START;
  const dayStartMinutes = timeToMinutes(dayStart);

  const eventTemplatesMap = mapBy(eventTemplates, '_id');
  const venueTemplatesMap = mapBy(venueTemplates, '_id');
  const eventTemplateTranslationsMap = mapBy(eventTemplateTranslations, 'refers');

  function getItemStatus(item: EventTableItem) {
    if (item.type === 'event') {
      const focusVisible = evaluateMcmEndpoint(
        'visible',
        focusEdp,
        item._data.event.mcm,
        item._data.eventTemplate.mcm
      );

      const focusTrending = evaluateMcmEndpoint(
        'trending',
        focusEdp,
        item._data.event.mcm,
        item._data.eventTemplate.mcm
      );

      const zoneVisible = focusZone
        ? evaluateAudienceZone(
            'visible',
            focusZone,
            item._data.event.audience,
            item._data.eventTemplate.audience
          )
        : true;

      const allVisible =
        evaluateMcmTruthyAggregate(
          'visible',
          mode === 'mcm' ? mcmVisibleEdps : serverInfo.mcmEndpoints,
          item._data.event.mcm,
          item._data.eventTemplate.mcm
        ) &&
        evaluateAudienceTruthyAggregate(
          'visible',
          property.zones.map(z => z._id),
          item._data.event.audience,
          item._data.eventTemplate.audience
        );

      const allTrending = evaluateMcmTruthyAggregate(
        'trending',
        mode === 'mcm' ? mcmVisibleEdps : serverInfo.mcmEndpoints,
        item._data.event.mcm,
        item._data.eventTemplate.mcm
      );

      const visible = mode === 'focus' ? focusVisible && zoneVisible : allVisible;
      const trending = mode === 'focus' ? focusTrending : allTrending;

      const translActive = focusLang
        ? ((eventTemplateTranslationsMap[item._data.eventTemplate._id]?.transforms ?? {})[focusLang]
            ?.active ?? false)
        : !item._data.eventTemplate.notNative;

      const translEvtTmpl = translateEventTemplate(
        item._data.eventTemplate,
        eventTemplateTranslationsMap[item._data.eventTemplate._id],
        focusLang
      );

      let varsActive: boolean | null = null;

      const defaultLangVarSet =
        hasAllMergedVarsSet(
          item._data.eventTemplate.title,
          undefined,
          serverInfo.defaultLanguage.key,
          item._data.event.mergedVars
        ) &&
        hasAllMergedVarsSet(
          item._data.eventTemplate.description,
          undefined,
          serverInfo.defaultLanguage.key,
          item._data.event.mergedVars
        );

      const focusLangVarSet =
        !translEvtTmpl ||
        (hasAllMergedVarsSet(
          translEvtTmpl.title,
          focusLang,
          serverInfo.defaultLanguage.key,
          item._data.event.mergedVars
        ) &&
          hasAllMergedVarsSet(
            translEvtTmpl.description,
            focusLang,
            serverInfo.defaultLanguage.key,
            item._data.event.mergedVars
          ));

      if (defaultLangVarSet) {
        varsActive = true;

        if (mode === 'focus' && focusLang && !focusLangVarSet) {
          varsActive = null;
        }
      } else {
        varsActive = false;
      }

      const hiddenFromBasicFilters = isHiddenEventDueToBasicFilters(
        item._data.eventTemplate,
        item._data.event.venue
      );

      return {
        visible:
          visible !== false &&
          !hiddenFromBasicFilters &&
          (props.mode === 'focus' ? translActive : true),
        hiddenFromBasicFilters,
        expanded: item.id === expandedReqId,
        trending,
        translActive,
        varsActive,
      };
    } else {
      return {
        visible: true,
        hiddenFromBasicFilters: false,
        expanded: item.id === expandedReqId,
        trending: true,
        translActive: true,
        varsActive: true,
      };
    }
  }

  const eventItems = plan.events
    .map(e => {
      const et = eventTemplatesMap[e.template];
      const vt = venueTemplatesMap[e.venue];

      if (!et || !vt) return undefined;

      const commonTexts = getCommonTexts(
        {
          default: serverInfo.brand.commonTexts,
          translations: commonTextTranslations,
        },
        { selected: focusLang }
      );

      const event = populateEvent(e, eventTemplates, venueTemplates, staffTemplates, plan.day);

      const eventTime =
        mode === 'focus'
          ? generateFormattedTimeString(
              e.startTime,
              e.duration,
              hourFormat,
              DEFAULT_TIMESTR_FORMAT,
              commonTexts,
              e.interactiveFeedDisplay,
              et.interactiveFeedDisplay,
              undefined
            )
          : getBasicTimeString(event, hourFormat);

      const [startTime, endTime] = eventTime.split(' - ');

      const venueName =
        mode === 'focus'
          ? getEventVenueDisplay(e, vt, commonTexts, 'name-only').venueString
          : vt.name;

      const evtTmplTransl = translateEventTemplate(
        et,
        eventTemplateTranslationsMap[et._id],
        focusLang
      );

      const eventTitle =
        mode === 'focus'
          ? evaluateMergedVars(
              evtTmplTransl?.title ?? et.title,
              dayBundle,
              serverInfo.languages.map(l => l.key),
              focusLang,
              serverInfo.defaultLanguage.key,
              e.mergedVars
            )[0]
          : et.title;

      const eventDescription =
        mode === 'focus'
          ? evaluateMergedVars(
              evtTmplTransl?.description ?? et.description,
              dayBundle,
              serverInfo.languages.map(l => l.key),
              focusLang,
              serverInfo.defaultLanguage.key,
              e.mergedVars
            )[0]
          : et.description;

      const result: EventTableItem = {
        id: e._id,
        type: 'event',
        segment: et.segment,
        title: eventTitle,
        description: eventDescription,
        venue: venueName,
        startTime,
        endTime,
        _data: {
          event,
          eventTemplate: et,
        },
      };

      return result;
    })
    .filter(notUndefined);

  const groupItems = groupEvts
    .map(e => {
      const vt = venueTemplatesMap[e.venue._id];

      if (!vt) return undefined;

      const [startTime, endTime] = getBasicTimeString(e, hourFormat).split(' - ');

      const result: EventTableItem = {
        id: e._id,
        type: 'groupEvent',
        title: e.title,
        description: '',
        venue: vt.name,
        startTime,
        endTime,
        _data: { event: e },
      };

      return result;
    })
    .filter(notUndefined);

  const tableItems = [...eventItems, ...groupItems].sort((a, b) =>
    makeBareEventSorter(dayStartMinutes)(a._data.event, b._data.event)
  );

  const fuse = useMemo(() => {
    return new Fuse(tableItems, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['title', 'venue'],
    });
  }, [tableItems]);

  const fuseSearchResults = useMemo(() => {
    if (!search || !fuse) return undefined;
    return fuse.search(search).map(s => s.item);
  }, [fuse, search]);

  const filteredTableItems = fuseSearchResults || tableItems;

  const evtFields: Array<BubbleTableFields<EventTableItem>> = [
    {
      key: 'time',
    },
    {
      key: 'hints',
      label: '',
      tdProps: { className: 'whitespace-nowrap p-1-i text-right' },
    },
    {
      key: 'title',
      tdProps: { className: 'w-full' },
    },
    {
      key: 'actions-1',
      label: '',
      tdProps: { className: 'whitespace-nowrap p-1-i text-right' },
    },
    {
      key: 'actions-2',
      label: '',
      tdProps: { className: 'whitespace-nowrap p-1-i text-right' },
    },
    {
      key: 'minimizer',
      label: '',
      tdProps: { className: 'whitespace-nowrap p-1-i pr-3-i text-right' },
    },
  ];

  function makeDefaultTemplateTdProps(
    data: EventTableItem,
    fieldTdProps: React.TdHTMLAttributes<HTMLTableCellElement>
  ) {
    const { visible, expanded } = getItemStatus(data);

    return {
      ...fieldTdProps,
      className: [
        fieldTdProps && fieldTdProps.className ? fieldTdProps.className : undefined,
        data.type === 'groupEvent' ? 'bg-forest text-white' : undefined,
        data.type !== 'groupEvent' && expanded ? 'bg-bubblegum-lightest' : undefined,
        !visible ? 'italic text-slate-lighter' : undefined,
      ]
        .filter(notUndefined)
        .join(' '),
    };
  }

  const evtTemplates: Array<BubbleTableTemplate<EventTableItem>> = [
    {
      key: 'time',
      tdProps: makeDefaultTemplateTdProps,
      render: item => {
        return (
          <>
            <div className="font-semibold whitespace-nowrap">{item.startTime}</div>
            <div className="whitespace-nowrap">{item.endTime}</div>
          </>
        );
      },
    },
    {
      key: 'hints',
      tdProps: makeDefaultTemplateTdProps,
      render: item => {
        let pricedHint = <></>;
        let simpleHint = <></>;

        if (item.type === 'event' && item._data.eventTemplate.priced) {
          pricedHint = (
            <BubbleBadge
              variant="lime"
              style={{ width: 15, height: 15 }}
              className="p-0-i grid items-center justify-center text-center "
              circle={true}
            >
              <FontAwesomeIcon icon="dollar-sign" />
            </BubbleBadge>
          );
        }

        if (item.type === 'event' && item._data.eventTemplate.simple) {
          simpleHint = (
            <>
              <BubbleBadge
                variant="ocean"
                style={{ width: 15, height: 15 }}
                className="p-0-i grid items-center justify-center text-center"
                circle={true}
              >
                <FontAwesomeIcon icon="s" />
              </BubbleBadge>
            </>
          );
        }

        return (
          <div className="flex items-center justify-end gap-1">
            <div className="w-4 flex items-center justify-center">{pricedHint}</div>
            <div className="w-4 flex items-center justify-center">{simpleHint}</div>
          </div>
        );
      },
    },
    {
      key: 'title',
      tdProps: makeDefaultTemplateTdProps,
      render: item => {
        return (
          <>
            <div className="font-semibold">
              <div className="flex items-center gap-1">
                {item.segment && (
                  <FontAwesomeIcon fixedWidth icon="circle" color={item.segment.color} />
                )}

                {item.type === 'event' ? (
                  <BubbleMergedVarHint mergedVars={item._data.event.mergedVars} text={item.title} />
                ) : (
                  <>{item.title}</>
                )}
              </div>
            </div>
            <div>{item.venue}</div>
          </>
        );
      },
    },
    {
      key: 'actions-1',
      tdProps: (item, props) => {
        const result = makeDefaultTemplateTdProps(item, props);
        if (mode === 'default' || mode === 'mcm') {
          result.className += ' hidden';
        }
        return result;
      },
      render: item => {
        const { translActive, varsActive } = getItemStatus(item);

        if (item.type === 'event') {
          let statuses = <></>;

          if (mode === 'focus') {
            statuses = (
              <>
                <BubbleBadge variant={translActive ? 'smoke' : 'crimson'} className="mt-1 mr-1">
                  <FontAwesomeIcon
                    fixedWidth
                    icon={
                      translActive
                        ? 'language'
                        : findIconDefinition({ prefix: 'fak', iconName: 'solid-language-slash' })
                    }
                  />
                </BubbleBadge>
                <BubbleBadge
                  variant={varsActive ? 'smoke' : varsActive === null ? 'banana' : 'crimson'}
                  className="mt-1"
                >
                  <FontAwesomeIcon
                    fixedWidth
                    icon={
                      varsActive || varsActive === null
                        ? 'dollar'
                        : findIconDefinition({ prefix: 'fak', iconName: 'solid-dollar-sign-slash' })
                    }
                  />
                </BubbleBadge>
              </>
            );
          }

          return <>{statuses}</>;
        } else {
          return <></>;
        }
      },
    },
    {
      key: 'actions-2',
      tdProps: makeDefaultTemplateTdProps,
      render: item => {
        const { visible, trending, hiddenFromBasicFilters } = getItemStatus(item);

        if (item.type === 'event') {
          let deleteActions = <></>;
          let edpActions = <></>;

          if (mode === 'default') {
            if (!locked) {
              deleteActions = (
                <>
                  {!showDelete[item.id] && (
                    <BubbleButton
                      className="p-2-i hover-item"
                      variant="crimson"
                      inverse
                      onClick={() => {
                        setShowDelete({ ...showDelete, [item.id]: true });
                      }}
                      icon="trash-alt"
                    />
                  )}
                  {showDelete[item.id] && (
                    <>
                      <BubbleButton
                        className="p-2-i hover-item"
                        variant="crimson"
                        inverse
                        onClick={() => {
                          onDeleteEvent(item.id);
                        }}
                        icon="trash-check"
                      />
                      <BubbleButton
                        className="p-2-i hover-item"
                        variant="slate"
                        inverse
                        onClick={() => {
                          setShowDelete({ ...showDelete, [item.id]: false });
                        }}
                        icon="trash-xmark"
                      />
                    </>
                  )}
                </>
              );
            }
            edpActions = (
              <>
                <BubbleButton
                  className="p-2-i"
                  variant={visible === null ? 'banana' : visible ? 'navy' : 'smoke'}
                  inverse
                  icon={visible === null ? 'eye' : visible ? 'eye' : 'eye-slash'}
                  onClick={() => {
                    if (item.type === 'event') {
                      const evt = {
                        ...item._data.event,
                        mcm: toggleMcmEndpoint(
                          'visible',
                          'all',
                          serverInfo.mcmEndpoints,
                          item._data.event.mcm,
                          item._data.eventTemplate.mcm
                        ),
                      };
                      onUpdateEvent(evt);
                    }
                  }}
                />
                <BubbleButton
                  className="p-2-i"
                  variant={trending === null ? 'banana' : trending ? 'crimson' : 'smoke'}
                  inverse
                  icon="fire"
                  onClick={() => {
                    if (item.type === 'event') {
                      const evt = {
                        ...item._data.event,
                        mcm: toggleMcmEndpoint(
                          'trending',
                          'all',
                          serverInfo.mcmEndpoints,
                          item._data.event.mcm,
                          item._data.eventTemplate.mcm
                        ),
                      };
                      onUpdateEvent(evt);
                    }
                  }}
                />
              </>
            );
          } else if (mode === 'mcm') {
            /* LABS-4777: Always show hidden eye icon if basic filters would hide the content,
            regardless of the MCM status at the time*/
            edpActions = (
              <>
                {mcmVisibleEdps.map(edp => {
                  const mcmVisible =
                    !hiddenFromBasicFilters &&
                    evaluateMcmEndpoint(
                      'visible',
                      edp,
                      item._data.event.mcm,
                      item._data.eventTemplate.mcm
                    );

                  return (
                    <BubbleButton
                      key={edp}
                      className="p-2-i"
                      variant={mcmVisible ? 'navy' : 'smoke'}
                      inverse
                      icon={mcmEndpointToIcon(edp, mcmVisible)}
                      onClick={() => {
                        const evt = {
                          ...item._data.event,
                          mcm: toggleMcmEndpoint(
                            'visible',
                            edp,
                            serverInfo.mcmEndpoints,
                            item._data.event.mcm,
                            item._data.eventTemplate.mcm
                          ),
                        };
                        onUpdateEvent(evt);
                      }}
                    />
                  );
                })}
              </>
            );
          } else if (mode === 'focus') {
            const mcmVisible =
              !hiddenFromBasicFilters &&
              evaluateMcmEndpoint(
                'visible',
                focusEdp,
                item._data.event.mcm,
                item._data.eventTemplate.mcm
              );

            edpActions = (
              <BubbleButton
                key={focusEdp}
                className="p-2-i"
                variant={mcmVisible ? 'navy' : 'smoke'}
                inverse
                icon={mcmEndpointToIcon(focusEdp, mcmVisible)}
                onClick={() => {
                  const evt = {
                    ...item._data.event,
                    mcm: toggleMcmEndpoint(
                      'visible',
                      focusEdp,
                      serverInfo.mcmEndpoints,
                      item._data.event.mcm,
                      item._data.eventTemplate.mcm
                    ),
                  };
                  onUpdateEvent(evt);
                }}
              />
            );
          }
          return (
            <fieldset disabled={locked}>
              {deleteActions}
              {edpActions}
            </fieldset>
          );
        } else {
          return <></>;
        }
      },
    },
    {
      key: 'minimizer',
      tdProps: makeDefaultTemplateTdProps,
      render: item => (
        <BubbleButton
          className="p-2-i"
          variant={item.type === 'event' ? 'navy' : 'white'}
          inverse
          icon={
            expandedReqId === item.id
              ? 'arrow-down-left-and-arrow-up-right-to-center'
              : 'arrow-up-right-and-arrow-down-left-from-center'
          }
          onClick={() => {
            setExpandedReqId(expandedReqId === item.id ? undefined : item.id);
          }}
        />
      ),
    },
  ];

  function tableDetailsRender(item: EventTableItem) {
    switch (item.type) {
      case 'groupEvent':
        return (
          <div className="py-3">
            <GroupsGlance
              user={user}
              hideComments
              hideStatusChange
              hideVenueDayView
              property={property}
              groupId={item._data.event.groupBooking}
              bookingId={item._data.event.groupRequest}
            />
          </div>
        );
      default:
        return (
          <div className="pb-3 pt-2">
            <div className="bg-white border shadow px-4 py-3 relative">
              <EventsGlance
                locked={locked}
                event={item._data.event}
                title={item.title}
                venue={item.venue}
                time={[item.startTime, item.endTime].filter(notFalsy).join(' - ')}
                description={item.description}
                onUpdateEvent={onUpdateEvent}
              />
              {!locked && (
                <div className="absolute top-0 right-0 flex flex-col">
                  <BubbleButton
                    variant="navy"
                    inverse
                    icon="edit"
                    onClick={() => {
                      onEditEvent(item._data.event);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        );
    }
  }

  const groupCount = tableItems.filter(ti => ti.type === 'groupEvent').length;

  const evtCountData = tableItems.filter(ti => ti.type === 'event').map(getItemStatus);
  const visibleCount = evtCountData.filter(ti => ti.visible === true).length;
  const notVisibleCount = evtCountData.filter(ti => ti.visible === false).length;
  const partialVisibleCount = evtCountData.filter(ti => ti.visible === null).length;

  return (
    <BubbleTable
      {...rest}
      responsive
      slim
      striped
      hideHeader
      trClassName="hover-visible"
      className="shadow-none-i rounded-0-i"
      keyExtractor={itm => itm.id}
      onRowDblClick={item => {
        if (!locked && item.type === 'event') {
          onEditEvent(item._data.event);
        }
      }}
      fields={evtFields}
      items={filteredTableItems}
      templates={evtTemplates}
      detailsKey={expandedReqId}
      detailsRender={tableDetailsRender}
      detailsClassName="bg-bubblegum-lightest"
      stickyHeader={37}
      showPretitle
      pretitleClassName="p-0-i"
      pretitleRender={() => (
        <div
          style={{ width: props.width }}
          className="flex space-x-2 no-scrollbar items-center p-2 bg-eggshell border-b overflow-x-auto"
        >
          <BubbleBadge variant="forest" className="flex-shrink-0">
            <FontAwesomeIcon icon="people-group" className="mr-1" />
            <span>{groupCount}</span>
          </BubbleBadge>
          <BubbleBadge variant="navy" className="flex-shrink-0">
            <FontAwesomeIcon icon="list" className="mr-1" />
            <span>{visibleCount + partialVisibleCount + notVisibleCount}</span>
          </BubbleBadge>
          <BubbleBadge variant="banana" className="flex-shrink-0">
            <FontAwesomeIcon icon="eye" className="mr-1" />
            <span>{visibleCount + partialVisibleCount}</span>
          </BubbleBadge>
          <BubbleBadge variant="smoke" className="flex-shrink-0">
            <FontAwesomeIcon icon="eye-slash" className="mr-1" />
            <span>{notVisibleCount}</span>
          </BubbleBadge>
          {mcmVisibleEdps
            .filter(edp => (mode === 'focus' ? focusEdp === edp : true))
            .map(edp => (
              <BubbleBadge key={edp} variant="navy" className="flex-shrink-0">
                <FontAwesomeIcon icon={mcmEndpointToIcon(edp, true)} className="mr-1" />
                <span>
                  {
                    tableItems
                      .map(
                        ti =>
                          ti.type === 'event' &&
                          getItemStatus(ti).visible &&
                          evaluateMcmEndpoint(
                            'visible',
                            edp,
                            ti._data.event.mcm,
                            ti._data.eventTemplate.mcm
                          )
                      )
                      .filter(notFalsy).length
                  }
                </span>
              </BubbleBadge>
            ))}
        </div>
      )}
    />
  );
}

export default DailyEventListPanel;
